// ---- Domain types for the constructor plans feature ----

export interface ConstructorService {
  id: number
  stage_id?: number
  service_name?: string
  name?: string
  quantity?: number
  qty?: number
  price?: number
  unit_price?: number
  original_unit_price?: number | null
  discounted_unit_price?: number | null
  total_price?: number
  tooth_number?: string
  tooth?: string
  sort_order?: number
  display_order?: number
  is_executed?: boolean | number
}

export interface ConstructorStage {
  id: number
  plan_id?: number
  title?: string
  name?: string
  stage_name?: string
  description?: string
  sort_order?: number
  display_order?: number
  status?: string
  doctor_id?: number | null
  doctor_name?: string | null
  cash_offer?: StageCashOffer | null
  services?: ConstructorService[]
  items?: ConstructorService[]
}

export interface StageCashOffer {
  amount?: number | null
  note?: string | null
  deadline?: string | null
  method?: string | null
}

export interface PlanInstallmentPayment {
  id?: number | null
  stage_id?: number | null
  stage_title?: string | null
  title: string
  amount?: number | null
  due_date?: string | null
  sort_order?: number | null
  status?: string | null
  note?: string | null
}

export interface PlanInstallment {
  enabled: boolean
  apply_to?: string | null
  stage_id?: number | null
  months?: number | null
  amount?: number | null
  rate?: number | null
  start_date?: string | null
  note?: string | null
  schedule?: PlanInstallmentPayment[]
}

export interface ConstructorPlan {
  id: number
  title?: string
  description?: string
  status?: string
  patient_id?: number
  doctor_id?: number
  patient_name?: string
  doctor_name?: string
  total_amount?: number
  plan_date?: string | null
  created_at?: string
  updated_at?: string
  stages?: ConstructorStage[]
  share?: {
    is_active?: boolean
    token?: string
    url?: string
    expires_at?: string
    hide_prices?: boolean
    payment?: unknown
  } | null
  share_in_patient_lk?: boolean
  media?: Array<{ id: number; file_name: string; file_type?: string; storage_path?: string }>
  materials?: Array<{ id: number; type?: string; title?: string; file_size_formatted?: string; created_at?: string; share_in_patient_lk?: boolean }>
  installment?: PlanInstallment | null
  public_theme?: 'blue' | 'gold' | string | null
  offer_deadline?: string | null
  note?: string | null
  plan_cash_offer?: StageCashOffer | null
  analytics?: PlanAnalytics | null
  capabilities?: { has_coefficients?: boolean; supports_installment?: boolean }
  mis_plan_id?: string | null
  version?: number | string | null
  is_ai_composed?: boolean
  workflow_checkpoints?: Record<string, import('./workflowCheckpoint').PlanWorkflowCheckpoint>
}

export interface PlanAnalyticsEvent {
  event?: string
  created_at?: string | null
  payload?: Record<string, unknown>
}

export interface PlanAnalytics {
  view_count?: number | null
  last_viewed_at?: string | null
  metadata?: Record<string, unknown>
  events?: PlanAnalyticsEvent[]
}

export interface ConstructorPlansResponse {
  plans?: ConstructorPlan[]
  success?: boolean
}

export interface ConstructorPlanDetailResponse {
  plan?: ConstructorPlan
  data?: { plan?: ConstructorPlan; share?: ConstructorPlan['share'] }
  success?: boolean
}

export interface MisServiceItem {
  id: string
  service_name: string
  tooth_number?: string
  quantity?: number
  unit_price?: number
  total_price?: number
  source_doctor_id?: number | null
  source_doctor_name?: string | null
  mis_plan_uuid?: string | null
}

export interface MisDragItem {
  planId: number | null
  misPlanUuid?: string | null
  service: MisServiceItem
}

export interface InstallmentPaymentDraft {
  key: string
  id?: number | null
  stage_id: string
  stage_title?: string | null
  title: string
  amount: string
  due_date: string
  sort_order: number
  status: string
  note: string
}

export type DoctorOption = { id: number; display_name: string; role_label: string }

// ---- Pure helper functions ----

export function getStageName(stage: ConstructorStage): string {
  return stage.title ?? stage.stage_name ?? `Этап ${stage.id}`
}

export function getServiceName(svc: ConstructorService): string {
  return svc.service_name ?? svc.name ?? ''
}

export function getServiceQty(svc: ConstructorService): number {
  return svc.quantity ?? svc.qty ?? 1
}

export function getServiceToothNumber(svc: ConstructorService): string {
  return svc.tooth_number ?? svc.tooth ?? ''
}

export function getServices(stage: ConstructorStage): ConstructorService[] {
  return stage.services ?? stage.items ?? []
}

export function getStageSortOrder(stage: ConstructorStage): number {
  return stage.sort_order ?? stage.display_order ?? 0
}

export function getServiceSortOrder(svc: ConstructorService): number {
  return svc.sort_order ?? svc.display_order ?? 0
}

export function parseDecimalInput(value: string): number | null {
  const normalized = value.trim().replace(/\s+/g, '').replace(',', '.')
  if (normalized === '') return null
  const parsed = Number(normalized)
  return Number.isFinite(parsed) ? parsed : null
}

export function formatPlainNumber(value?: number | null): string {
  return value === null || value === undefined || Number.isNaN(value) ? '' : String(value)
}

export function toDateInputValue(value?: string | null): string {
  return value ? value.slice(0, 10) : ''
}

export function defaultOfferDeadline(): string {
  const date = new Date()
  date.setDate(date.getDate() + 7)
  return date.toISOString().slice(0, 10)
}

export function addMonthsToDate(value: string, months: number): string {
  if (!value) return ''
  const date = new Date(`${value}T00:00:00`)
  if (Number.isNaN(date.getTime())) return ''
  date.setMonth(date.getMonth() + months)
  return date.toISOString().slice(0, 10)
}

export function normalizeDoctorName(value?: string | null): string {
  return (value ?? '').trim().toLowerCase().replace(/\s+/g, ' ')
}

export function planCreatedTimestamp(plan: Pick<ConstructorPlan, 'created_at' | 'updated_at'>): number {
  const primary = plan.created_at ? Date.parse(plan.created_at) : Number.NaN
  if (Number.isFinite(primary)) return primary
  const fallback = plan.updated_at ? Date.parse(plan.updated_at) : Number.NaN
  return Number.isFinite(fallback) ? fallback : 0
}

export function comparePlansByCreatedAtDesc(a: ConstructorPlan, b: ConstructorPlan): number {
  const diff = planCreatedTimestamp(b) - planCreatedTimestamp(a)
  return diff !== 0 ? diff : b.id - a.id
}

export function isExecuted(svc: ConstructorService): boolean {
  return Boolean(svc.is_executed)
}

export function getMisPlanServicesFromDetail(detail: {
  id: number
  doctor_id?: number | null
  doctor_name?: string | null
  stages: Array<{
    id?: number
    services: Array<{
      id?: number
      service_name?: string | null
      tooth_number?: string | null
      quantity?: number | null
      unit_price?: number | null
      total_price?: number | null
    }>
  }>
}): MisServiceItem[] {
  return detail.stages.flatMap((stage, stageIndex) => (
    stage.services.map((service, serviceIndex) => ({
      id: `${detail.id}-${stage.id ?? stageIndex}-${service.id ?? serviceIndex}`,
      service_name: service.service_name ?? 'Услуга',
      tooth_number: service.tooth_number ?? '',
      quantity: service.quantity ?? 1,
      unit_price: service.unit_price ?? undefined,
      total_price: service.total_price ?? undefined,
      source_doctor_id: typeof detail.doctor_id === 'number' ? detail.doctor_id : null,
      source_doctor_name: detail.doctor_name ?? null,
    }))
  ))
}

// ---- Installment distribution helpers ----

export function distributeInstallmentAmount(amount: number | null, count: number): string[] {
  if (amount === null || count <= 0) return Array.from({ length: Math.max(count, 0) }, () => '')
  const total = Math.round(amount)
  const base = Math.floor(total / count)
  let remainder = total - base * count

  return Array.from({ length: count }, () => {
    const value = base + (remainder > 0 ? 1 : 0)
    remainder = Math.max(0, remainder - 1)
    return String(value)
  })
}

export function createMonthlyInstallmentRows(
  amount: number | null,
  months: number | null,
  startDate: string,
): InstallmentPaymentDraft[] {
  const count = Math.max(1, Math.trunc(months ?? 0))
  const amounts = distributeInstallmentAmount(amount, count)

  return Array.from({ length: count }, (_, index) => ({
    key: `month-${index + 1}-${startDate || 'no-date'}`,
    stage_id: '',
    stage_title: null,
    title: `Платёж ${index + 1}`,
    amount: amounts[index] ?? '',
    due_date: startDate ? addMonthsToDate(startDate, index) : '',
    sort_order: index,
    status: 'planned',
    note: '',
  }))
}

export function redistributeInstallmentRows(
  rows: InstallmentPaymentDraft[],
  amount: number | null,
  changedIndex: number,
): InstallmentPaymentDraft[] {
  if (amount === null || changedIndex < 0 || changedIndex >= rows.length - 1) {
    return rows
  }

  const fixedTotal = rows.slice(0, changedIndex + 1).reduce(
    (sum, row) => sum + (parseDecimalInput(row.amount) ?? 0),
    0,
  )
  const remainingRows = rows.length - changedIndex - 1
  const remainingTotal = Math.max(0, amount - fixedTotal)
  const amounts = distributeInstallmentAmount(remainingTotal, remainingRows)

  return rows.map((row, index) => (
    index > changedIndex
      ? { ...row, amount: amounts[index - changedIndex - 1] ?? '' }
      : row
  ))
}
